import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import { useCart } from "@/contexts/CartContext";

type Step = "shipping" | "payment" | "confirmation";

interface ShippingInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

export default function CheckoutPage() {
  const { items, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("shipping");
  const [shipping, setShipping] = useState<ShippingInfo>({
    firstName: "", lastName: "", email: "", phone: "",
    address: "", city: "", state: "", zip: "",
  });
  const [errors, setErrors] = useState<Partial<ShippingInfo>>({});

  const shippingCost = totalPrice >= 500 ? 0 : 49;
  const tax = totalPrice * 0.08;
  const orderTotal = totalPrice + shippingCost + tax;

  const validateShipping = () => {
    const newErrors: Partial<ShippingInfo> = {};
    if (!shipping.firstName) newErrors.firstName = "Required";
    if (!shipping.lastName) newErrors.lastName = "Required";
    if (!shipping.email || !/\S+@\S+\.\S+/.test(shipping.email)) newErrors.email = "Valid email required";
    if (!shipping.address) newErrors.address = "Required";
    if (!shipping.city) newErrors.city = "Required";
    if (!shipping.state) newErrors.state = "Required";
    if (!shipping.zip) newErrors.zip = "Required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleShippingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateShipping()) setStep("payment");
  };

  if (items.length === 0 && step !== "confirmation") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Your cart is empty.</p>
          <Link to="/products" className="text-[#2D5016] hover:underline cursor-pointer">Shop Now</Link>
        </div>
      </div>
    );
  }

  const steps: { key: Step; label: string }[] = [
    { key: "shipping", label: "Shipping" },
    { key: "payment", label: "Payment" },
    { key: "confirmation", label: "Confirmation" },
  ];

  const stepIndex = steps.findIndex(s => s.key === step);

  return (
    <div className="min-h-screen bg-[#F9F7F3]">
      <Navbar />
      <main className="pt-[72px]">
        <div className="max-w-6xl mx-auto px-6 py-10">
          {/* Progress */}
          <div className="flex items-center justify-center gap-0 mb-12">
            {steps.map((s, i) => (
              <div key={s.key} className="flex items-center">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  i <= stepIndex ? "text-[#2D5016]" : "text-gray-300"
                }`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    i < stepIndex ? "bg-[#2D5016] text-white" :
                    i === stepIndex ? "bg-[#2D5016] text-white" :
                    "bg-gray-200 text-gray-400"
                  }`}>
                    {i < stepIndex ? <i className="ri-check-line"></i> : i + 1}
                  </div>
                  <span className="whitespace-nowrap">{s.label}</span>
                </div>
                {i < steps.length - 1 && (
                  <div className={`w-12 h-0.5 ${i < stepIndex ? "bg-[#2D5016]" : "bg-gray-200"}`}></div>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Form */}
            <div className="flex-1">
              {step === "shipping" && (
                <div className="bg-white rounded-2xl p-8">
                  <h2 className="text-xl font-bold text-[#1a1a1a] mb-6">Shipping Information</h2>
                  <form onSubmit={handleShippingSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">First Name *</label>
                        <input
                          type="text"
                          value={shipping.firstName}
                          onChange={e => setShipping({ ...shipping, firstName: e.target.value })}
                          className={`w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#2D5016] transition-colors ${errors.firstName ? "border-red-300" : "border-gray-200"}`}
                          placeholder="John"
                        />
                        {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Last Name *</label>
                        <input
                          type="text"
                          value={shipping.lastName}
                          onChange={e => setShipping({ ...shipping, lastName: e.target.value })}
                          className={`w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#2D5016] transition-colors ${errors.lastName ? "border-red-300" : "border-gray-200"}`}
                          placeholder="Doe"
                        />
                        {errors.lastName && <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email Address *</label>
                      <input
                        type="email"
                        value={shipping.email}
                        onChange={e => setShipping({ ...shipping, email: e.target.value })}
                        className={`w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#2D5016] transition-colors ${errors.email ? "border-red-300" : "border-gray-200"}`}
                        placeholder="john@example.com"
                      />
                      {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Phone Number</label>
                      <input
                        type="tel"
                        value={shipping.phone}
                        onChange={e => setShipping({ ...shipping, phone: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#2D5016] transition-colors"
                        placeholder="(602) 555-0100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Street Address *</label>
                      <input
                        type="text"
                        value={shipping.address}
                        onChange={e => setShipping({ ...shipping, address: e.target.value })}
                        className={`w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#2D5016] transition-colors ${errors.address ? "border-red-300" : "border-gray-200"}`}
                        placeholder="123 Main Street"
                      />
                      {errors.address && <p className="text-red-400 text-xs mt-1">{errors.address}</p>}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div className="col-span-2 sm:col-span-1">
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">City *</label>
                        <input
                          type="text"
                          value={shipping.city}
                          onChange={e => setShipping({ ...shipping, city: e.target.value })}
                          className={`w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#2D5016] transition-colors ${errors.city ? "border-red-300" : "border-gray-200"}`}
                          placeholder="Phoenix"
                        />
                        {errors.city && <p className="text-red-400 text-xs mt-1">{errors.city}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">State *</label>
                        <input
                          type="text"
                          value={shipping.state}
                          onChange={e => setShipping({ ...shipping, state: e.target.value })}
                          className={`w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#2D5016] transition-colors ${errors.state ? "border-red-300" : "border-gray-200"}`}
                          placeholder="AZ"
                        />
                        {errors.state && <p className="text-red-400 text-xs mt-1">{errors.state}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">ZIP Code *</label>
                        <input
                          type="text"
                          value={shipping.zip}
                          onChange={e => setShipping({ ...shipping, zip: e.target.value })}
                          className={`w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#2D5016] transition-colors ${errors.zip ? "border-red-300" : "border-gray-200"}`}
                          placeholder="85015"
                        />
                        {errors.zip && <p className="text-red-400 text-xs mt-1">{errors.zip}</p>}
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-[#2D5016] hover:bg-[#3d6826] text-white font-semibold py-4 rounded-full transition-colors cursor-pointer text-sm tracking-wide whitespace-nowrap mt-2"
                    >
                      Continue to Payment
                    </button>
                  </form>
                </div>
              )}

              {step === "payment" && (
                <div className="bg-white rounded-2xl p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <button onClick={() => setStep("shipping")} className="text-gray-400 hover:text-[#2D5016] transition-colors cursor-pointer flex items-center justify-center w-8 h-8">
                      <i className="ri-arrow-left-line"></i>
                    </button>
                    <h2 className="text-xl font-bold text-[#1a1a1a]">Payment</h2>
                  </div>

                  {/* Shipping Summary */}
                  <div className="bg-[#F9F7F3] rounded-xl p-4 mb-6 text-sm text-gray-500">
                    <div className="flex items-center justify-between">
                      <span>Shipping to: <strong className="text-[#1a1a1a]">{shipping.firstName} {shipping.lastName}</strong></span>
                      <button onClick={() => setStep("shipping")} className="text-[#2D5016] text-xs hover:underline cursor-pointer whitespace-nowrap">Edit</button>
                    </div>
                    <p className="mt-1">{shipping.address}, {shipping.city}, {shipping.state} {shipping.zip}</p>
                  </div>

                  {/* Stripe Notice */}
                  <div className="border-2 border-dashed border-[#2D5016]/30 rounded-xl p-8 text-center mb-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-[#EEF5E8] rounded-full mx-auto mb-4">
                      <i className="ri-bank-card-line text-2xl text-[#2D5016]"></i>
                    </div>
                    <h3 className="font-semibold text-[#1a1a1a] mb-2">Stripe Payment Integration</h3>
                    <p className="text-gray-400 text-sm mb-4 max-w-sm mx-auto">
                      To enable live payments, connect your Stripe account. Your customers will be able to pay securely with any major credit card.
                    </p>
                    <div className="flex items-center justify-center gap-2 text-gray-300 text-sm">
                      <i className="ri-shield-check-line text-[#2D5016]"></i>
                      <span>256-bit SSL encryption &bull; PCI compliant</span>
                    </div>
                  </div>

                  <button
                    onClick={() => { setStep("confirmation"); clearCart(); }}
                    className="w-full bg-[#2D5016] hover:bg-[#3d6826] text-white font-semibold py-4 rounded-full transition-colors cursor-pointer text-sm tracking-wide whitespace-nowrap"
                  >
                    Complete Order — ${orderTotal.toFixed(2)}
                  </button>
                  <p className="text-center text-gray-400 text-xs mt-3 flex items-center justify-center gap-1">
                    <i className="ri-lock-line"></i> Secured by Stripe
                  </p>
                </div>
              )}

              {step === "confirmation" && (
                <div className="bg-white rounded-2xl p-10 text-center">
                  <div className="w-20 h-20 flex items-center justify-center bg-[#EEF5E8] rounded-full mx-auto mb-6">
                    <i className="ri-checkbox-circle-line text-4xl text-[#2D5016]"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-[#1a1a1a] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Order Confirmed!
                  </h2>
                  <p className="text-gray-500 text-sm mb-2">
                    Thank you, {shipping.firstName}! Your order has been placed successfully.
                  </p>
                  <p className="text-gray-400 text-xs mb-8">
                    A confirmation email will be sent to <strong>{shipping.email}</strong>
                  </p>
                  <div className="bg-[#F9F7F3] rounded-xl p-5 mb-8 text-left">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-400">Order Total</span>
                      <span className="font-bold text-[#1a1a1a]">${orderTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Estimated Delivery</span>
                      <span className="font-medium text-[#1a1a1a]">5-10 Business Days</span>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate("/products")}
                    className="bg-[#2D5016] text-white px-10 py-3 rounded-full text-sm font-semibold hover:bg-[#3d6826] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Continue Shopping
                  </button>
                </div>
              )}
            </div>

            {/* Order Summary Sidebar */}
            {step !== "confirmation" && (
              <div className="w-full lg:w-[340px] flex-shrink-0">
                <div className="bg-white rounded-2xl p-6 sticky top-24">
                  <h3 className="font-bold text-[#1a1a1a] mb-5">Order Summary</h3>
                  <div className="space-y-3 mb-5 max-h-64 overflow-y-auto">
                    {items.map(item => (
                      <div key={item.product.id} className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#F9F7F3] flex-shrink-0">
                          <img src={item.product.images[0]} alt={item.product.name} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-[#1a1a1a] truncate">{item.product.name}</p>
                          <p className="text-xs text-gray-400">Qty: {item.quantity}</p>
                        </div>
                        <span className="text-sm font-semibold text-[#1a1a1a] whitespace-nowrap">${(item.product.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-100 pt-4 space-y-2">
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Subtotal</span>
                      <span>${totalPrice.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Shipping</span>
                      <span className={shippingCost === 0 ? "text-[#2D5016] font-medium" : ""}>{shippingCost === 0 ? "FREE" : `$${shippingCost}`}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Tax</span>
                      <span>${tax.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-gray-100 pt-3 flex justify-between font-bold text-[#1a1a1a]">
                      <span>Total</span>
                      <span>${orderTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
